import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSendStatusReport, useStatusReportDraft } from "@/hooks/use-status-report";

const SLACK_CHANNELS = [
  { key: "dept-post-delivery", label: "dept-post-delivery" },
  { key: "delivery-compliance", label: "delivery-compliance" },
] as const;

type SlackChannelKey = (typeof SLACK_CHANNELS)[number]["key"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StatusReportDialog({ open, onOpenChange }: Props) {
  const { data, isLoading, isError } = useStatusReportDraft(open);
  const send = useSendStatusReport();

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [selectedKeys, setSelectedKeys] = useState<Set<SlackChannelKey>>(new Set());

  useEffect(() => {
    if (open && data) {
      setSubject(data.subject);
      setBody(data.draft);
    }
    if (!open) {
      setSelectedKeys(new Set());
    }
  }, [open, data]);

  function toggleChannel(key: SlackChannelKey) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleSend() {
    send.mutate(
      { subject, body, slackChannelKeys: [...selectedKeys] },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  const recipients = data?.recipients ?? [];
  const postDelivery = data?.teams?.postDelivery ?? [];
  const deliveryCompliance = data?.teams?.deliveryCompliance ?? [];

  const canSend =
    !isLoading &&
    !isError &&
    !send.isPending &&
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    (recipients.length > 0 || selectedKeys.size > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Audit Status Report</DialogTitle>
          <DialogDescription>
            Review and edit the draft below, then send it to configured recipients and any selected
            Slack channels.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Email recipients */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Email Recipients
            </Label>
            {isLoading ? (
              <div className="space-y-1.5">
                <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                <div className="h-4 bg-muted rounded animate-pulse w-2/4" />
              </div>
            ) : recipients.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No recipients configured — add at least one team in Settings → System.
              </p>
            ) : (
              <div className="space-y-1 text-sm">
                <div className="flex gap-2">
                  <span className="text-muted-foreground w-40 shrink-0">Post Delivery</span>
                  {postDelivery.length > 0 ? (
                    <span className="break-all">{postDelivery.join(", ")}</span>
                  ) : (
                    <span className="text-muted-foreground italic">Not configured</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <span className="text-muted-foreground w-40 shrink-0">Delivery Compliance</span>
                  {deliveryCompliance.length > 0 ? (
                    <span className="break-all">{deliveryCompliance.join(", ")}</span>
                  ) : (
                    <span className="text-muted-foreground italic">Not configured</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Slack channels */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Slack Channels
              {selectedKeys.size > 0 && (
                <span className="ml-2 text-foreground">({selectedKeys.size} selected)</span>
              )}
            </Label>
            <div className="rounded-md border border-border">
              {SLACK_CHANNELS.map((ch) => {
                const selected = selectedKeys.has(ch.key);
                return (
                  <button
                    key={ch.key}
                    type="button"
                    onClick={() => toggleChannel(ch.key)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors hover:bg-muted/50 first:rounded-t-md last:rounded-b-md ${
                      selected ? "bg-muted/30" : ""
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        selected
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {selected && <Check className="h-3 w-3" />}
                    </span>
                    <span className="text-muted-foreground">#</span>
                    {ch.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subject */}
          <div className="space-y-1.5">
            <Label htmlFor="sr-subject">Subject</Label>
            <Input
              id="sr-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={isLoading}
              placeholder="Audit Status Report"
            />
          </div>

          {/* Body */}
          <div className="space-y-1.5">
            <Label htmlFor="sr-body">Report Body</Label>
            {isLoading ? (
              <div className="h-64 bg-muted rounded-md animate-pulse" />
            ) : isError ? (
              <p className="text-sm text-destructive">
                Failed to generate report draft. Please try again.
              </p>
            ) : (
              <Textarea
                id="sr-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={16}
                className="font-mono text-xs resize-y"
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={send.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!canSend}>
            {send.isPending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
